// Copyright 2026 Hardfin, Inc.
// Hardfin Confidential - Restricted
// Template download step for the company data upload workflow.
// Displays required and optional CSV fields, and provides a downloadable template.
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface Props {
  onBack: () => void;
  onNext: () => void;
}

/**
 * Displays the CSV template requirements for company data and provides a download button.
 *
 * @param props.onBack - Callback to close the dialog
 * @param props.onNext - Callback to advance to the CSV upload step
 */
export function TemplateDownload(props: Props) {
  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Name',
      'Is customer',
      'Is supplier',
      'Billing address',
      'Comment',
      'External ID',
      'Domain',
    ];

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `company_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for company data
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              <Typography variant="body2">Name - text</Typography>
            </li>
            <li>
              <Typography variant="body2">Is customer - true/false</Typography>
            </li>
            <li>
              <Typography variant="body2">Is supplier - true/false</Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Available fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">Billing address - text</Typography>
            </li>
            <li>
              <Typography variant="body2">Comment - text</Typography>
            </li>
            <li>
              <Typography variant="body2">External ID - text</Typography>
            </li>
            <li>
              <Typography variant="body2">Domain - text</Typography>
            </li>
          </ul>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Any fields included in your upload will overwrite existing data,
              including empty cells
            </li>
            <li>
              You can delete optional columns (only "Name", "Is customer", and
              "Is supplier" are required)
            </li>
            <li>
              For targeted updates, only include the specific names and fields
              you want to modify
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
